import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import some from 'lodash/some';
import { Step2ValueModel } from '../types';


import {
    validateOrganisationNumber,
    OrganisationType,
    validateCompanyName,
    validateZipCode,
    validateCity,
} from '../../shared/Validation';
import { Step2Props } from './types';
import { NumberType } from '../types';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import { StatusCode } from "@apps/Shared/Services/Response.types";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";
import Icon from "@sharedComponents/Icon";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import InputText from "@sharedComponents/InputText";
import InputToggleButton from "@sharedComponents/InputToggleButton";
import InputSelect from "@sharedComponents/InputSelect";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import CompanyInformationsService from "@apps/Shared/Services/CompanyInformations/CompanyInformations.service";
import {
    validateContactEmail,
    validateCountry, validateStep2,
    validateStreetAddress,
    validateVatNumber,
    validateSupplierType
} from "@apps/CompanyRegistrations/RegisterFlow/Forms/ProducerForm/Validation";
import { ArticleSupplierType } from "@apps/Shared/Services/CompanyInformations/interfaces";
import LoadingIndicator from "@sharedComponents/LoadingIndicator";
import { ColumnType, SortDirection } from "@sharedComponents/Table/types";
import Table from "@sharedComponents/Table";

const Step2: FunctionComponent<Step2Props> = props => {
    const { t } = useTranslation()

    const [loadingCompanyInfo, setLoadingCompanyInfo] = useState<boolean>(false)
    const [loadingSupplierType, setLoadingSupplierType] = useState<boolean>(
        false
    )
    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const [supplierTypes, setSupplierTypes] = useState<ArticleSupplierType[]>(
        []
    )
    const allErrors = getAllErrors(formErrors)

    const [countries, setCountries] = useState([])

    const [stepValues, setStepValues] = useState<Step2ValueModel>({
        organizationNumber: props.values.organizationNumber || '',
        vatNumber: props.values.vatNumber || '',
        registeredForFTax: 'false',
        registeredForVAT: 'false',
        numberType: props.values.numberType,
        selectedSupplierType: [],
    })

    const getCountries = () => {
        CompanyRegistrationsService.GetCountries().then((result) => {
            if (result && result.status === 200) {
                setCountries(result.data)
            }
        })
    }

    const rehydrate = (values: any) => {
        if (values) {
            // Rehydrate form values when switching between steps,
            // or when a saved registration is opened
            setStepValues({
                ...stepValues,
                organizationNumber: values.organizationNumber || '',
                vatNumber: values.vatNumber || '',
                companyName: values.companyName || '',
                registeredForFTax: values.registeredForFTax || 'false',
                registeredForVAT: values.registeredForVAT || 'false',
                streetAddress: values.streetAddress || '',
                zipCode: values.zipCode || '',
                city: values.city || '',
                country: values.country || '',
                email: values.email || '',
                selectedSupplierType: values.selectedSupplierType,
            })
        }
        if (values.selectedSupplierType) {
            setSupplierTypes(values.selectedSupplierType)
        } else {
            setLoadingSupplierType(true)
            CompanyInformationsService.GetSupplierTypes()
                .then((result: any) => {
                    if (result.status === StatusCode.OK) {
                        setSupplierTypes(result.data)
                        setLoadingSupplierType(false)
                    } else {
                        const errorMessages = result.data.message
                        console.log(errorMessages)
                        setLoadingSupplierType(false)
                    }
                })
                .catch((error: any) => {
                    console.log(error)
                    setLoadingSupplierType(false)
                })
        }
    }

    useEffect(() => {
        // When rehydrate key toggles, regardless what value it has, reload values.
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        if (!props.values.requestInfo) {
            return
        }

        if (props.values.numberType === NumberType.VATNumber) {
            getCountries()
        }

        setLoadingCompanyInfo(true)

        setLoadingSupplierType(true)
        CompanyInformationsService.GetSupplierTypes()
            .then((result: any) => {
                if (result.status === StatusCode.OK) {
                    setSupplierTypes(result.data)
                    setLoadingSupplierType(false)
                } else {
                    const errorMessages = result.data.message
                    console.log(errorMessages)
                    setLoadingSupplierType(false)
                }
            })
            .catch((error: any) => {
                console.log(error)
                setLoadingSupplierType(false)
            })

        CompanyRegistrationsService.GetSupplierCompanyInfo(
            props.values.organizationNumber || props.values.vatNumber,
            props.values.numberType
        )
            .then((result: any) => {
                if (result && result.status === 200) {
                    const company: any = result.data

                    if (company.organizationNumber || company.vatNumber) {
                        const map: Step2ValueModel = {
                            ...company,
                            organizationNumber:
                                stepValues.numberType ===
                                NumberType.OrganisationNumber
                                    ? stepValues.organizationNumber
                                    : '',
                            vatNumber:
                                stepValues.numberType === NumberType.VATNumber
                                    ? stepValues.vatNumber
                                    : '',
                            registeredForVAT:
                                company.registeredForVAT === null
                                    ? 'false'
                                    : company.registeredForVAT === true
                                    ? 'true'
                                    : 'false',
                            registeredForFTax:
                                company.registeredForFTax === null
                                    ? 'false'
                                    : company.registeredForFTax === true
                                    ? 'true'
                                    : 'false',
                        }

                        setStepValues(map)
                        props.onChangeValues && props.onChangeValues(map)
                    }
                    setLoadingCompanyInfo(false)
                } else {
                    setLoadingCompanyInfo(false)
                }
            })
            .catch((error: any) => {
                console.log(error)
                setLoadingCompanyInfo(false)
            })

        rehydrate(props.values)
    }, [])

    const onChangeOrgNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, organizationNumber: value })
        props.onChangeValues &&
            props.onChangeValues({ organizationNumber: value })

        props.onChangeValues && props.onChangeValues({ requestInfo: true })

        validateOrganisationNumber(
            OrganisationType.Supplier,
            value,
            formErrors,
            false,
            true
        ).then(result => {
            setFormErrors(result?.errors)
        })
    }

    const onChangeVatNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, vatNumber: value })
        props.onChangeValues && props.onChangeValues({ vatNumber: value })
        props.onChangeValues && props.onChangeValues({ requestInfo: true })
        validateVatNumber(value, formErrors, false, true).then(errors => {
            setFormErrors(errors)
        })
    }

    const onChangeCompanyName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, companyName: value })
        props.onChangeValues && props.onChangeValues({ companyName: value })
        const errors = validateCompanyName(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeFTax = (value: string) => {
        setStepValues({ ...stepValues, registeredForFTax: value })
        props.onChangeValues &&
            props.onChangeValues({ registeredForFTax: value })
    }

    const onChangeRegisteredForVat = (value: string) => {
        setStepValues({ ...stepValues, registeredForVAT: value })
        props.onChangeValues &&
            props.onChangeValues({ registeredForVAT: value })
    }

    const onChangeStreetAddress = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, streetAddress: value })
        props.onChangeValues && props.onChangeValues({ streetAddress: value })
        const errors = validateStreetAddress(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, zipCode: value })
        props.onChangeValues && props.onChangeValues({ zipCode: value })
        const validateZipCodeFormat =
            props.values.numberType === NumberType.OrganisationNumber
        const errors = validateZipCode(value, validateZipCodeFormat, formErrors)
        setFormErrors(errors)
    }

    const onChangeCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, city: value })
        props.onChangeValues && props.onChangeValues({ city: value })
        const errors = validateCity(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeCountries = (option: InputSelectOption) => {
        const value = option
        setStepValues({ ...stepValues, country: value })
        props.onChangeValues && props.onChangeValues({ country: value })
        const errors = validateCountry(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, email: value })
        props.onChangeValues && props.onChangeValues({ email: value })
        const errors = validateContactEmail(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeSupplierType = (
        e: ChangeEvent<HTMLInputElement>,
        rowData: ArticleSupplierType
    ) => {
        const checked = !!e.target.checked
        const newSupplierTypes = [...supplierTypes]
        const index = newSupplierTypes.findIndex(
            type => type.code === rowData.code
        )

        if (index !== -1) {
            if (checked) {
                newSupplierTypes[index].isType = true
            } else {
                newSupplierTypes[index].isType = false
            }
            setSupplierTypes(newSupplierTypes)
            const value = newSupplierTypes;

            //Update step values:
            setStepValues({
                ...stepValues,
                selectedSupplierType: newSupplierTypes,
            })
            props.onChangeValues &&
                props.onChangeValues({ selectedSupplierType: newSupplierTypes })

            const errors = validateSupplierType(value, formErrors)
            setFormErrors(errors)
        }
    }

    const nextButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
            <span>{t('button.next')}</span>
            <Icon iconName="arrow-right" />
        </span>
    )

    const nextStep = () => {
        validateStep2(stepValues, props.values.numberType, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.onChangeValues &&
                        props.onChangeValues({ requestInfo: false })
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.newRegistration && props.newRegistration()
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const formatOptions = (rowData: ArticleSupplierType) => {
        return (
            <div className='buttons-wrapper'>
                <InputCheckbox
                    id={`same-${rowData.code}-checkbox-input`}
                    label={rowData.description}
                    hideLabel={true}
                    checked={!!rowData.isType}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChangeSupplierType(e, rowData)
                    }
                />
            </div>
        )
    }

    const renderArticleSupplierTypeListItemTable = (
        articleSupplierTypeItem: ArticleSupplierType[]
    ) => {
        const items = [...articleSupplierTypeItem]
        let columns: ColumnType[] = [
            {
                text: t('global.articleSupplierType'),
                key: 'description',
                sortable: false,
            },
        ]
        columns = columns.concat([
            {
                text: t('companyInformation.chooseSupplierType'),
                key: 'options',
                sortable: false,
                format: (cellData: any, rowData: ArticleSupplierType) =>
                    formatOptions(rowData),
            },
        ])

        const tableProps = {
            caption: t('companyRegistration.customer.typeOfSupplier'),
            columns: columns,
            columnsMobile: ['description', 'options'],
            data: items || [],
            defaultDirection: 'asc' as SortDirection,
            defaultSortKey: 'none',
        }

        return <Table {...tableProps} />
    }

    const renderArticleSupplierType = (
        articleSupplierTypeItem: ArticleSupplierType[]
    ) => {
        return (
            <>
                <div key={``} className={!!some(formErrors, ['key', 'supplierType']) ? 'supplier-type-error' : ''}>
                    {renderArticleSupplierTypeListItemTable(
                        articleSupplierTypeItem
                    )}
                    {!!some(formErrors, ['key', 'supplierType']) && (
                        <div className="alert alert--danger alert--input">
                            {uniqueErrors('supplierType')}
                        </div>
                    )}
                </div>
            </>
        )
    }

    const cancelButtonText = (
        <span className='buttons-wrapper__button-text'>
            <Icon iconName="close" />
            <span>{t('button.cancel')}</span>
        </span>
    )

    const prevButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
            <Icon iconName="arrow-left" />
            <span>{t('button.previous')}</span>
        </span>
    )

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>{t('companyRegistration.stepLabel.step2')}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            <div className='fields-wrapper'>
                {loadingCompanyInfo && (
                    <div className='fields-wrapper__overlay'>
                        <LoadingIndicator />
                    </div>
                )}
                <div className='fields-wrapper__field'>
                    {props.values.numberType ===
                    NumberType.OrganisationNumber ? (
                        <InputText
                            id="org-number"
                            label={t(
                                'companyRegistration.organisationNumberLabel'
                            )}
                            value={stepValues.organizationNumber || ''}
                            hasError={
                                !!some(formErrors, [
                                    'key',
                                    'organizationNumber',
                                ])
                            }
                            disabled={true}
                            required={true}
                            onChange={onChangeOrgNumber}
                            autoComplete="off"
                        />
                    ) : (
                        <InputText
                            id="vat-number"
                            label={t('companyRegistration.vatNumberLabel')}
                            value={stepValues.vatNumber || ''}
                            hasError={!!some(formErrors, ['key', 'vatNumber'])}
                            disabled={true}
                            required={true}
                            onChange={onChangeVatNumber}
                            autoComplete="off"
                        />
                    )}
                </div>

                <div className='fields-wrapper__field'>
                    <InputText
                        id="company-name"
                        label={t('companyRegistration.companyName')}
                        value={stepValues.companyName || ''}
                        hasError={!!some(formErrors, ['key', 'companyName'])}
                        required={true}
                        maxLength={50}
                        onChange={onChangeCompanyName}
                        autoComplete="organization"
                        errorText={uniqueErrors('companyName')}
                    />
                </div>

                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="f-tax"
                        className="toggle-field"
                        label={`${t('companyRegistration.fTax')}*`}
                        options={[
                            {
                                value: 'true',
                                label: t('companyRegistration.yes'),
                            },
                            {
                                value: 'false',
                                label: t('companyRegistration.no'),
                            },
                        ]}
                        value={stepValues.registeredForFTax}
                        onSelect={onChangeFTax}
                    />
                </div>

                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="registered-for-vat"
                        label={`${t('companyRegistration.registeredForVat')}*`}
                        className="toggle-field"
                        options={[
                            {
                                value: 'true',
                                label: t('global.yes'),
                            },
                            {
                                value: 'false',
                                label: t('global.no'),
                            },
                        ]}
                        value={stepValues.registeredForVAT}
                        onSelect={onChangeRegisteredForVat}
                    />
                </div>
                <div className='form-step-wrapper'>
                    <div className='fields-wrapper'>
                        <div className='fields-wrapper__field'>
                            {loadingSupplierType ? (
                                <LoadingIndicator customMargins="5rem 0" />
                            ) : (
                                <>{renderArticleSupplierType(supplierTypes)}</>                                
                            )}
                        </div>
                    </div>
                </div>
                <h4>{t('companyRegistration.invoiceAddress')}</h4>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="street-address"
                        label={t('companyRegistration.streetAddress')}
                        value={stepValues.streetAddress || ''}
                        required={true}
                        maxLength={100}
                        onChange={onChangeStreetAddress}
                        hasError={!!some(formErrors, ['key', 'streetAddress'])}
                        autoComplete="street-address"
                        errorText={uniqueErrors('streetAddress')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="zip-code"
                        label={t('companyRegistration.zipCode')}
                        value={stepValues.zipCode || ''}
                        required={true}
                        onChange={onChangeZipCode}
                        hasError={!!some(formErrors, ['key', 'zipCode'])}
                        autoComplete="postal-code"
                        errorText={uniqueErrors('zipCode')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="city"
                        label={t('companyRegistration.city')}
                        value={stepValues.city || ''}
                        required={true}
                        maxLength={50}
                        onChange={onChangeCity}
                        hasError={!!some(formErrors, ['key', 'city'])}
                        autoComplete="city"
                        errorText={uniqueErrors('city')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    {props.values.numberType === NumberType.VATNumber ? (
                        <InputSelect
                            emptyMessage={t('error.emptyCountry')}
                            hasError={!!some(formErrors, ['key', 'country'])}
                            id="country"
                            hideLabel={false}
                            label={t('global.country')}
                            onChange={onChangeCountries}
                            options={countries}
                            required={true}
                            errorText={uniqueErrors('country')}
                            value={stepValues.country}
                        />
                    ) : (
                        ''
                    )}
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="email"
                        label={t('companyRegistration.email')}
                        value={stepValues.email || ''}
                        required={true}
                        onChange={onChangeEmail}
                        hasError={!!some(formErrors, ['key', 'email'])}
                        type="email"
                        autoComplete="email"
                        maxLength={250}
                        errorText={uniqueErrors('email')}
                    />
                </div>
            </div>
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={nextButtonText}
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={prevButtonText}
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={cancelButtonText}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step2